import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  School,
  PlayArrow,
  RestartAlt,
  DeleteForever,
} from "@mui/icons-material";
import api from "../../utils/api";
import { useNavigate } from "react-router-dom";
import PageContainer from "../../components/PageContainer";
export default function StudentSubjectsPage() {
  const navigate = useNavigate();

  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeExam, setActiveExam] = useState(null);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // ==========================================================
  // LOAD SUBJECTS
  // ==========================================================
  const loadSubjects = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/student/subjects");

      const filtered = (res.data.subjects || []).filter((s) => {
        const code = String(s.code || "").trim().toUpperCase();
        return !["POST_UTME", "POSTUTME", "PUTME"].includes(code);
      });

      setSubjects(filtered);
    } catch {
      setToast({
        open: true,
        message: "Error loading subjects",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // CHECK ACTIVE EXAM (SERVER-AUTHORITATIVE)
  // ==========================================================
  const checkActiveExam = async () => {
    try {
      const res = await api.get("/exam/active");
      if (res.data?.hasActiveExam) {
        setActiveExam(res.data.exam);
      } else {
        setActiveExam(null);
      }
    } catch {
      setActiveExam(null);
    }
  };

  useEffect(() => {
    loadSubjects();
    checkActiveExam();
  }, []);

  const hasPostUTMEActive = activeExam?.exam_type === "POST_UTME";
  const hasJambActive = activeExam?.exam_type === "JAMB";
  const isActiveSubjectExam = activeExam?.exam_type === "SUBJECT_PRACTICE";

  // ==========================================================
  // ACTIONS
  // ==========================================================
  const handleRegister = async (subjectId) => {
    if (hasJambActive) return;

    try {
      setActionLoading(subjectId);
      await api.post("/api/student/subjects/register", {
        subject_id: subjectId,
      });
      setToast({
        open: true,
        message: "Subject registered successfully.",
        severity: "success",
      });
      loadSubjects();
    } catch (err) {
      setToast({
        open: true,
        message: err.response?.data?.message || "Failed to register subject",
        severity: "error",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartExam = async (subjectId) => {
    if (hasJambActive) return;

    // Resume subject practice if already active
    if (
      isActiveSubjectExam &&
      activeExam?.subject_id === subjectId &&
      activeExam?.id
    ) {
      navigate(`/exam/session/${activeExam.id}`, { replace: true });
      return;
    }

    try {
      setActionLoading(subjectId);
      const res = await api.post("/exam/start", {
        exam_type: "SUBJECT_PRACTICE",
        subject_id: subjectId,
      });
      if (res.data?.exam?.id) {
        navigate(`/exam/session/${res.data.exam.id}`);
      }
    } catch (err) {
      setToast({
        open: true,
        message: err.response?.data?.message || "Cannot start exam",
        severity: "error",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleClear = async (subjectId, status) => {
    if (hasJambActive) return;

    if (status === "in_progress") {
      setToast({
        open: true,
        message: "You must finish the exam before clearing.",
        severity: "warning",
      });
      return;
    }

    try {
      setActionLoading(subjectId);
      await api.post("/api/student/subjects/reset", {
        subject_id: subjectId,
      });
      setToast({
        open: true,
        message: "Progress reset successfully.",
        severity: "success",
      });
      loadSubjects();
    } catch {
      setToast({
        open: true,
        message: "Error clearing subject",
        severity: "error",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // ==========================================================
  // STATUS CHIP
  // ==========================================================
  const StatusChip = ({ status }) => {
    const map = {
      none: { label: "Available", color: "success" },
      registered: { label: "Registered", color: "primary" },
      in_progress: { label: "In Progress", color: "warning" },
      completed: { label: "Completed", color: "secondary" },
    };
    const item = map[status] ?? map.none;
    return (
      <Chip
        label={item.label}
        color={item.color}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  // ==========================================================
  // UI
  // ==========================================================
  return (
     <PageContainer>
    <Box p={2}>
      <Typography variant="h5" fontWeight={700} mb={1}>
        <School sx={{ mr: 1 }} /> Subject Practice
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Select a subject to practice. You can only take one exam at a time.
      </Typography>

      {activeExam && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography fontWeight={700}>Active exam detected</Typography>
          <Typography variant="body2">
            Exam type: <strong>{activeExam.exam_type}</strong>
          </Typography>

          {hasJambActive ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              ⚠️ JAMB exams can only be resumed from the JAMB page.
            </Typography>
          ) : (
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 1 }}
              onClick={() =>
                navigate(`/exam/session/${activeExam.id}`, { replace: true })
              }
            >
              Resume Exam
            </Button>
          )}
        </Alert>
      )}

      {hasPostUTMEActive && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Subject practice is locked while a Post-UTME exam is active.
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {subjects.map((s) => {
            const isBusy = actionLoading === s.subject_id;
            const showResume =
              s.registered_status === "in_progress" &&
              isActiveSubjectExam &&
              activeExam?.subject_id === s.subject_id;

            const disableAll = hasPostUTMEActive || hasJambActive || isBusy;

            return (
              <Grid item xs={12} sm={6} md={4} key={s.subject_id}>
                <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {s.name}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Code: {s.code}
                  </Typography>

                  <Box mt={1}>
                    <StatusChip status={s.registered_status} />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" flexDirection="column" gap={1}>
                    {s.registered_status === "none" && (
                      <Button
                        variant="outlined"
                        disabled={disableAll}
                        onClick={() => handleRegister(s.subject_id)}
                        startIcon={
                          isBusy && (
                            <CircularProgress size={16} color="inherit" />
                          )
                        }
                      >
                        {isBusy ? "Registering…" : "Register Subject"}
                      </Button>
                    )}

                    {s.registered_status === "registered" && !showResume && (
                      <>
                        <Button
                          variant="contained"
                          disabled={disableAll}
                          onClick={() => handleStartExam(s.subject_id)}
                          startIcon={
                            isBusy ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <PlayArrow />
                            )
                          }
                        >
                          {isBusy ? "Starting Exam…" : "Start Practice"}
                        </Button>

                        <Tooltip title="Reset progress">
                          <Button
                            variant="text"
                            color="error"
                            disabled={disableAll}
                            onClick={() =>
                              handleClear(s.subject_id, s.registered_status)
                            }
                            startIcon={<DeleteForever />}
                          >

							{isBusy ? "Deleting..." : "Cancle Subject"}
                          </Button>
                        </Tooltip>
                      </>
                    )}

                    {showResume && (
                      <Button
                        variant="contained"
                        color="warning"
                        disabled={disableAll}
                        onClick={() => handleStartExam(s.subject_id)}
                        startIcon={
                          isBusy ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <RestartAlt />
                          )
                        }
                      >
                        {isBusy ? "Loading Session…" : "Resume Practice"}
                      </Button>
                    )}

                    {s.registered_status === "completed" && (
                      <Button
                        variant="outlined"
                        disabled={disableAll}
                        onClick={() => handleRegister(s.subject_id)}
                        startIcon={
                          isBusy ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <RestartAlt />
                          )
                        }
                      >
                        {isBusy ? "Preparing…" : "Practice Again"}
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.severity}>{toast.message}</Alert>
      </Snackbar>
    </Box>
		  </PageContainer>
  );
}
